import { useState, useEffect } from 'react';
import { PoolTable } from './PoolTable';
import { usePoolData } from '../hooks/usePoolData';

export function APRTracker() {
  const { poolGroups, loading, error, refreshing, refreshCurrentData } = usePoolData();

  // Auto-refresh state
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);
  const [autoRefreshMs, setAutoRefreshMs] = useState(120_000);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  // Auto-refresh effect
  useEffect(() => {
    if (!autoRefreshEnabled) return;

    const id = setInterval(() => {
      if (!refreshing) {
        refreshCurrentData();
      }
    }, autoRefreshMs);

    return () => clearInterval(id);
  }, [autoRefreshEnabled, autoRefreshMs, refreshCurrentData, refreshing]);

  // Update lastUpdated when data loads
  useEffect(() => {
    if (!loading && !refreshing) {
      setLastUpdated(new Date());
    }
  }, [loading, refreshing]);

  if (loading) {
    return (
      <div className="dashboard">
        <div className="loading">
          <div className="spinner"></div>
          <p>Loading pool data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard">
        <div className="error">
          <h2>Error Loading Data</h2>
          <p>{error}</p>
          <button onClick={refreshCurrentData} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <h1>Aerodrome APR Tracker</h1>
        <div className="header-controls">
          <div className="auto-refresh-controls">
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={autoRefreshEnabled}
                onChange={e => setAutoRefreshEnabled(e.target.checked)}
              />
              <span>Auto Refresh</span>
            </label>
            <select
              value={autoRefreshMs}
              onChange={e => setAutoRefreshMs(Number(e.target.value))}
              disabled={!autoRefreshEnabled}
            >
              <option value={120000}>2 min</option>
            </select>
            {lastUpdated && (
              <span className="last-updated">
                Last updated: {lastUpdated.toLocaleTimeString()}
              </span>
            )}
          </div>
          <button
            onClick={refreshCurrentData}
            className="refresh-button"
            disabled={refreshing}
          >
            {refreshing ? (
              <>
                <div className="spinner small"></div>
                Refreshing...
              </>
            ) : (
              'Refresh Data'
            )}
          </button>
        </div>
      </header>

      <div className="dashboard-description">
        <p>This screen shows Current APR of each pool along with a rolling history of APR performance. Note, Slipstream APRs on your My Positions screen will always show a smaller APR for your specific deposit price range.</p>
      </div>

      <main className="dashboard-content">
        <PoolTable poolGroups={poolGroups} />
      </main>

      <style>{`
        .dashboard-description {
          margin-bottom: 20px;
        }

        .dashboard-description p {
          margin: 0;
          color: #a0aec0;
          font-size: 14px;
          line-height: 1.5;
        }
      `}</style>
    </div>
  );
}
